import type { MenuItem } from '../../api/types'
import { formatZAR } from '../../lib/format'
import { useCart } from '../../cart/useCart'
import { priceStringToCents } from '../../cart/money'

interface Props {
  item: MenuItem
  onTap: (item: MenuItem) => void
}

export function ItemCard({ item, onTap }: Props) {
  const { addItem } = useCart()

  function handleQuickAdd(e: React.MouseEvent) {
    e.stopPropagation()
    addItem({
      menuItemId: item.id,
      name: item.name,
      unitPriceCents: priceStringToCents(item.price),
      imageThumbnail: item.image_thumbnail,
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Ignore keydowns bubbling up from the nested quick-add button (e.g. Enter/
    // Space while it's focused) — only the card div itself should open the sheet.
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onTap(item)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onTap(item)}
      onKeyDown={handleKeyDown}
      className="flex gap-4 w-full text-left rounded-[var(--card-radius)] p-4 bg-white border border-neutral-100 shadow-sm hover:shadow-lg hover:border-neutral-200 active:scale-[0.98] transition-all focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer"
      aria-label={`View details for ${item.name}`}
    >
      <div className="shrink-0 w-[120px] h-[120px] rounded-[var(--card-radius)] overflow-hidden bg-bg-page">
        {item.image_thumbnail ? (
          <img
            src={item.image_thumbnail}
            alt={item.name}
            width={120}
            height={120}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
            <svg
              className="w-8 h-8 text-neutral-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 20.25h18M3.75 3h16.5A.75.75 0 0121 3.75v12a.75.75 0 01-.75.75H3.75A.75.75 0 013 15.75v-12A.75.75 0 013.75 3z"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 min-w-0 flex-1 font-body">
        <p className="font-semibold text-sm leading-snug text-neutral-900">{item.name}</p>
        {item.description && (
          <p className="text-xs text-neutral-500 line-clamp-2 leading-relaxed">
            {item.description}
          </p>
        )}
        <div className="mt-auto pt-1 flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-brand-accent">{formatZAR(item.price)}</p>
          {item.is_available && (
            <button
              onClick={handleQuickAdd}
              aria-label={`Add ${item.name} to order`}
              className="shrink-0 w-8 h-8 rounded-full bg-brand-accent text-[var(--brand-accent-text)] flex items-center justify-center text-lg font-bold hover:opacity-90 active:scale-95 transition-all"
            >
              +
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
